/**
 * Spec-019 baked-asset SYNC (client half). The cloud persists a locked version's
 * baked layered-mockup assets as metadata + hosted asset refs (never pixels); on
 * sync the client must resolve those refs → bytes → save them locally (putMockup)
 * so render/export composite ALL layers on ANY device, and the template preview
 * (the baked `generated` image) shows. Without this the render silently falls back
 * to a classic design-only render and the export loses background/texture/shell.
 */
import type { FitMode, MockupAssets, Polygon, ShadingParams, VarnishParams } from "@genz/engine-bind";
import type { AppLogger } from "../app/context.js";
import { PRIMARY_VIEW } from "./views.js";
import { getMockup, putMockup, hasMockup, mockupKey } from "../stores/mockupStore.js";
import {
  joinInflight,
  trackInflight,
  getFailure,
  FAILURE_TTL_MS,
  markHydrating,
  markReady,
  setFailure,
} from "../stores/hydrationStore.js";
import { fetchWithTimeout, DOWNLOAD_TIMEOUT_MS, NO_HTTP_CACHE } from "./timeout.js";
import { isDownloadFailure, type DownloadErrorKind, type PlatformIO } from "@genz/platform";

/** Cloud baked-asset refs (mirrors the cloud `baked_assets` JSON — refs/geometry only). */
export interface BakedAssetRefs {
  /** Hosted product-on-white PNG (also the template preview). */
  generated_url: string;
  /** Hosted green-removed product shell PNG. */
  shell_url: string;
  /** Hosted product-mask image (grayscale; 255 = inside product). */
  mask_url: string;
  /** Hosted print-area-mask image (grayscale; 255 = inside the print region). Optional —
   *  when present, synced devices clip the design to the print area (clear-case fix). */
  print_mask_url?: string;
  /** Normalized 4-pt print quad (engine `Polygon` — round-trips bake → cloud → render). */
  print_quad: Polygon;
  clear_phone?: boolean;
  opacity?: MockupAssets["opacity"];
  /** Optional explicit preview ref (else `generated_url`). */
  preview_url?: string;
  /** Hosted URLs of the picked LAYER images (texture overlay + clear-case background)
   *  from the admin image library. Persisted so opening the template on ANOTHER system
   *  restores those layers (they were previously editor-local bytes → lost cross-device). */
  texture_url?: string;
  clear_bg_url?: string;
  /** Whether the "Texture / effect layer" was ON when the mockup was baked. Persisted EXPLICITLY
   *  because the layer's enabled state used to be INFERRED from `texture_url` being present — so a
   *  texture layer configured WITHOUT a picked image (the supported UV-varnish flow: "UV varnish
   *  needs no texture — it's generated from the design") had nothing recording that it was on, and
   *  reopening the template (here or on another device) silently turned the layer back off, taking
   *  its blend/varnish calibration out of the bake with it. */
  texture_on?: boolean;
  /** The texture layer's blend mode ("normal"|"multiply"|…|"uvvarnish"), so it round-trips too. */
  texture_blend?: string;
  /** Spot-UV varnish calibration for a "uvvarnish"-blend texture layer, saved so the effect +
   *  its settings restore on reopen and on another device. */
  varnish?: VarnishParams;
  /** "Rotate placement" — the design/texture rotation (0/90/180/270), saved with the template. */
  design_rotation?: number;
  /** How the design fills the print area ("contain"|"cover"|"stretch"|"height"|"width"). */
  design_fill?: string;
  /** Forced design aspect [w,h] (center-cropped), saved with the template. */
  design_aspect?: [number, number];
  /** Artwork-shading strength control (RAW 0..1, NOT the baked effective value), restored on
   *  reopen. The effective value used for render is `stage_shading === false ? 0 : print_shading`. */
  print_shading?: number;
  /** FULL engine shading calibration (strength + tuned constants + synthetic gradient/grain) —
   *  the exact object the editor preview composed with, so a synced device's batch render
   *  matches the authoring preview. Wins over `print_shading` in the engine when present. */
  shading?: ShadingParams;
  /** AI-removal pipeline stage toggles, restored on reopen. */
  stage_despill?: boolean;
  stage_refine?: boolean;
  stage_matte_strength?: boolean;
  stage_shading?: boolean;
  /** Matte quality mode ("fast"|"quality"), restored on reopen. */
  matte_quality?: "fast" | "quality";
  /** Hosted RAW AI-matte PNG (grayscale; 255 = kept object) from the on-device removal.
   *  Persisted so reopening the template restores the "background removed" state AND the
   *  AI-edge calibration slider (which reads the raw matte) — without re-running the ~12s
   *  segmenter. Present only when the background was removed with the on-device AI. */
  matte_url?: string;
  /** Whether the opt-in "AI edge" print mask was on, so its toggle restores on reopen. */
  ai_edge?: boolean;
  /** The "AI edge — protect content %" slider value (0..100), restored on reopen. */
  ai_edge_keep?: number;
  /** Per-template edge-calibration sliders, restored on reopen. 0/negative are meaningful
   *  (a saved 0/−N must override the global calibration default), so they persist explicitly. */
  edge_feather_px?: number;
  edge_choke_px?: number;
  matte_strength?: number;
  /** WHICH technique produced the cutout: the AI segmenter (`"ai"`, the default when absent) or
   *  the deterministic magenta chroma key (`"chroma"`). A chroma cutout is restored by RE-KEYING
   *  the pristine image, not by replaying `matte_url` through the AI-matte stages — replaying it
   *  discards the key's despilled colours and lets protect/choke/feather erode the panel. */
  matte_kind?: "ai" | "chroma";
  /** MARKER KEYING inputs (audit P1-1). The picked marker colour + its tolerance drive
   *  `detectMagentaMarker`, i.e. the SHELL and the PRINT MASK. They had no storage field at all, so
   *  reopening reset them to `null`/`0` and the next dirty Save re-keyed with the DEFAULT magenta and
   *  uploaded a wrong shell + print mask over the good ones. `marker_color` absent ⇒ the engine
   *  default keying (the same meaning `null` has in the editor), so only an explicit pick persists. */
  marker_color?: [number, number, number];
  marker_tol?: number;
  /** Explicit shaded-marker opt-in; absent means legacy keying for existing templates. */
  shaded_magenta_guide?: boolean;
  /** "Protect product" (audit P1-2) — restores the original alpha inside the print area during the
   *  matte apply, so it RESHAPES the product mask + shell. Same overwrite hazard as the marker
   *  fields: no storage field meant the next Save re-keyed with the mount default (`true`). */
  protect_product?: boolean;
  /** Product CATEGORY of the profile picker (audit P1-3) — "phone_case" | "uv_case" | "mug" | …. It
   *  was re-seeded from `baseType` on every open, so picking e.g. "Mug" was lost and the reopened
   *  editor re-enabled the clear phone layer. Free-form string on the wire (the editor's
   *  `ProductCategory` union) so this module stays independent of the editor's types. */
  product_category?: string;
}

/**
 * MULTI-VIEW baked-asset refs (Wave B). The cloud `baked_assets` JSON is single-view
 * today: the FLAT `BakedAssetRefs` fields describe the PRIMARY (back) view. This adds an
 * optional `views` map keyed by `view_type` for every OTHER view (back MAY also appear in
 * the map). Resolution rule: the map entry WINS over the flat fields when both are present.
 *
 * Old app builds ignore `views` and keep rendering the flat back view — so producers that
 * update the primary view must ALSO write the flat fields (see `mergeViewRefs`).
 */
export interface BakedAssetRefsMap extends BakedAssetRefs {
  views?: Record<string, BakedAssetRefs>;
}

/** Copy just the flat `BakedAssetRefs` fields out of a map (drop the `views` sub-map). */
function flatOf(refs: BakedAssetRefsMap): BakedAssetRefs {
  const { views: _views, ...flat } = refs;
  return flat;
}

/**
 * Resolve one view's baked refs from a `BakedAssetRefsMap`, or null if that view has none.
 *
 * - A `views[view]` entry always WINS (map wins over flat).
 * - Otherwise the flat legacy fields answer ONLY for the primary view, and ONLY when they
 *   actually carry assets (`generated_url` present) — a map that holds only OTHER views has
 *   empty flat fields, which must not resolve `back`.
 * - Any other view with no map entry → null.
 */
export function refsForView(
  refs: BakedAssetRefsMap | null | undefined,
  view: string,
): BakedAssetRefs | null {
  if (!refs) return null;
  const mapped = refs.views?.[view];
  if (mapped) return mapped;
  if (view === PRIMARY_VIEW && refs.generated_url) return flatOf(refs);
  return null;
}

/**
 * Read-modify-write a `BakedAssetRefsMap`: set `view`'s baked refs to `next`, returning a
 * NEW map (never mutates `existing` or `next`). Preserves every OTHER view's map entry AND
 * the legacy flat fields. When `view === PRIMARY_VIEW` it ALSO REPLACES the flat fields
 * wholesale with `next` (same full-overwrite semantics as the cloud's `setBakedAssets`), so
 * old app builds still render the primary view — but the `views` map itself is kept.
 */
export function mergeViewRefs(
  existing: BakedAssetRefsMap | null | undefined,
  view: string,
  next: BakedAssetRefs,
): BakedAssetRefsMap {
  const nextCopy: BakedAssetRefs = { ...next };
  const prevViews = existing?.views;
  const views: Record<string, BakedAssetRefs> = { ...prevViews, [view]: nextCopy };
  if (view === PRIMARY_VIEW) {
    // Replace the flat back-view fields WHOLESALE (no stale keys survive), but keep `views`.
    return { ...nextCopy, views };
  }
  // Preserve the existing flat fields (including a legacy view with no map entry) + `views`.
  const flat = existing ? flatOf(existing) : ({} as BakedAssetRefs);
  return { ...flat, views };
}

/**
 * Remove one or more views' entries from a `BakedAssetRefsMap`'s `views` sub-map, returning a NEW
 * map (never mutates `refs`). Used after a Save that DROPPED view drafts (the admin removed a view
 * tab) so those views' now-orphaned baked refs don't linger in the cloud — `mergeViewRefs`
 * deliberately PRESERVES sibling views, so a removed view is only cleaned up here.
 *
 * Only the `views` map is touched: the flat legacy (primary/back) fields are left intact, since the
 * primary view is never removable (the last view can't be dropped, and back is the primary). When
 * nothing matched, the SAME reference is returned so callers can skip a redundant persist. When the
 * last map entry is dropped, the empty `views` key is removed entirely.
 */
export function dropViewRefs(
  refs: BakedAssetRefsMap | null | undefined,
  viewTypes: string[],
): BakedAssetRefsMap | null | undefined {
  if (!refs || !refs.views) return refs;
  const drop = new Set(viewTypes);
  const kept: Record<string, BakedAssetRefs> = {};
  let changed = false;
  for (const [view, entry] of Object.entries(refs.views)) {
    if (drop.has(view)) { changed = true; continue; }
    kept[view] = entry;
  }
  if (!changed) return refs;
  const next: BakedAssetRefsMap = { ...refs };
  if (Object.keys(kept).length > 0) next.views = kept;
  else delete next.views;
  return next;
}

/**
 * A 1×1 fully-opaque WHITE PNG — the print-area COVERAGE stand-in for a "uvvarnish" texture layer
 * that has no texture image (see `hydrateOneView`). The editor bakes a full-resolution solid-opaque
 * PNG for exactly this case; the engine warps the fill to the print quad, so a 1×1 white pixel
 * expands to the identical uniform coverage (and its only other role — a faint grain multiply — is
 * a no-op at white). Literal bytes so it needs neither a canvas nor a base64 decoder.
 */
export const UV_VARNISH_COVERAGE_PNG: Uint8Array = Uint8Array.from([
  137, 80, 78, 71, 13, 10, 26, 10, 0, 0, 0, 13, 73, 72, 68, 82, 0, 0, 0, 1, 0, 0, 0, 1, 8, 6, 0, 0,
  0, 31, 21, 196, 137, 0, 0, 0, 13, 73, 68, 65, 84, 120, 218, 99, 248, 255, 255, 255, 127, 0, 9,
  251, 3, 253, 245, 216, 241, 154, 0, 0, 0, 0, 73, 69, 78, 68, 174, 66, 96, 130,
]);

export interface HydrateDeps {
  /** Fetch a hosted asset's bytes (desktop/web). */
  fetchBytes: (url: string) => Promise<Uint8Array>;
  /** Decode a mask image's bytes → row-major u8 coverage (255 inside the product). */
  decodeMask: (bytes: Uint8Array) => Promise<Uint8Array>;
}

/** How many mask pixels to copy per synchronous chunk before yielding to the event loop.
 *  ~1M pixels/chunk keeps each burst well under a frame budget while a multi-megapixel mask
 *  is decoded, so the width×height copy no longer blocks the paint path (visible freeze on
 *  render-page open). Exported so a test can assert the chunk boundary. */
export const DECODE_CHUNK_PX = 1_000_000;

/**
 * Copy a decoded image's red channel into a row-major u8 coverage buffer (255 = inside the
 * product), CHUNKED off the paint path: it processes `chunkPx` pixels synchronously, then
 * `await`s a `setTimeout(0)` so the UI can paint before the next burst. Byte-identical to the
 * old single-pass loop — only the scheduling changed. `chunkPx` defaults to `DECODE_CHUNK_PX`
 * and is injectable purely so tests can exercise the multi-chunk path on a tiny buffer.
 */
export async function decodeMaskCoverage(
  rgba: ArrayLike<number>,
  width: number,
  height: number,
  chunkPx: number = DECODE_CHUNK_PX,
): Promise<Uint8Array> {
  const cov = new Uint8Array(width * height);
  const step = Math.max(1, Math.floor(chunkPx)); // guard: a 0/negative arg must not spin forever
  for (let start = 0; start < cov.length; start += step) {
    const end = Math.min(start + step, cov.length);
    for (let i = start; i < end; i++) cov[i] = rgba[i * 4] ?? 0;
    if (end < cov.length) await new Promise((r) => setTimeout(r, 0)); // yield to paint
  }
  return cov;
}

/** Build HydrateDeps from a platform `io` (decodeImage) + global fetch. Shared by EVERY
 *  render entry point that must pull a synced template's baked assets down before render,
 *  so they don't each hand-roll (and drift on) the same fetch/decode wiring. */
/**
 * Transient download failures are RETRIED. A single blip must not fail a whole template.
 *
 * Baked assets are ~1 MB each and are served from Cloudflare's `pub-*.r2.dev` endpoint, which is
 * a DEVELOPMENT url and is rate-limited — measured from one machine, the same object took
 * between 0.6 s and 4.7 s, and on a slower link an occasional connection is dropped outright.
 * The browser reports that as a bare `TypeError: Failed to fetch`. With no retry, one such blip
 * marked the template failed until the operator manually forced another sync — which is exactly
 * the intermittent "N of M templates failed" that gets reported.
 *
 * Only TRANSIENT causes retry: a dropped/blocked request, 5xx, 408 and 429. A 404 or 403 is a
 * real answer — the object is missing or forbidden — and retrying it just delays the truth.
 *
 * EXPORTED because the baked assets are no longer the only hosted bytes a render needs: the
 * classic (no-bake) path pulls the template's CASE PHOTO from `base_case_image_ref`
 * (`stores/caseImage.ts`). Same origin, same bucket, same failure modes — so the same ladder,
 * rather than a second hand-rolled fetch that drifts from this one.
 */
const DOWNLOAD_ATTEMPTS = 3;
const RETRY_BACKOFF_MS = [400, 1200];

/** Whether `status` is worth another attempt (absent = the request never got a response). */
function isRetryableStatus(status?: number): boolean {
  if (status === undefined) return true; // transport failure — no response at all
  return status >= 500 || status === 408 || status === 429;
}

/** The DESKTOP transport (WS-W), when the platform offers one. Absent on web ⇒ `fetch`. */
type DownloadBytes = NonNullable<PlatformIO["downloadBytes"]>;

/**
 * Failure kinds that are the platform REFUSING, not the network failing — and so cannot come out
 * differently on attempt 2.
 *
 * `policy` (the url is off the allow-list, is not https, or names a non-default port),
 * `too-large` (the body is over the 64 MiB cap) and `redirect` (a 302 left the origin) are all
 * decided from the SAME url by the SAME code every time. Retrying them costs 1.6 s of backoff,
 * two more Rust invocations and three identical log lines per refused asset, and buys nothing.
 * Every other kind (`dns` / `connect` / `tls` / `timeout` / `body` / `request`) describes the
 * network on this attempt, which is exactly what a retry exists for — so this list is a closed
 * one and the ladder below is unchanged for everything not on it.
 */
const DETERMINISTIC_KINDS: ReadonlySet<DownloadErrorKind> = new Set(["policy", "too-large", "redirect"]);

/** The two operator-facing lines an ANSWERED request can produce. Written once so the same
 *  failure can never read differently on desktop and on web. */
const emptyBodyReason = (url: string): string => `GET ${loggableUrl(url)} -> 200 but the body was EMPTY`;
const statusReason = (url: string, status: number, statusText?: string): string =>
  `GET ${loggableUrl(url)} -> ${status} ${statusText ?? ""}`.trim();

/**
 * WS-W — on DESKTOP the bytes come from Rust (`PlatformIO.downloadBytes`), on web from `fetch`.
 * The ladder around the transport is the SAME either way, deliberately: a desktop build that
 * quietly retried a 404, or gave up on a 503, would be a different product on two platforms.
 *
 * Why the transport moved at all: on 2026-09-16 the desktop app failed every baked-asset
 * download from `assets.untangle.today` for over an hour with WebKit's status-less
 * `TypeError: Load failed`, while curl, `URLSession`, Safari and a stand-alone WKWebView on the
 * same Mac fetched the same objects fine — CORS, CSP, Screen Time, Bitdefender, ITP and HTTP/3
 * each ruled out with evidence. Whatever the exact trigger inside the web view's long-lived
 * networking process, the bytes that carry the product's mockup must not depend on it.
 */
export async function fetchBytesWithRetry(url: string, downloadBytes?: DownloadBytes): Promise<Uint8Array> {
  let lastError = "";
  let made = 0; // requests actually sent — the number the failure message reports
  for (let attempt = 1; attempt <= DOWNLOAD_ATTEMPTS; attempt++) {
    let status: number | undefined;
    // A 2xx whose body is empty is a truncated or glitched response, not an answer, so it is
    // retried like a dropped connection. `status` alone cannot say so (200 is not retryable),
    // hence the separate flag.
    let emptyBody = false;
    made = attempt;
    try {
      if (downloadBytes) {
        const res = await downloadBytes(url, { timeoutMs: DOWNLOAD_TIMEOUT_MS });
        if (isDownloadFailure(res)) {
          // NO HTTP response at all — `status` stays undefined, which is exactly how this ladder
          // already reads a thrown `TypeError: Failed to fetch`: transient, so retry. The reason
          // is the platform's own, and already names the url AND what the transport did (the
          // reqwest error kind), which is the diagnostic "Load failed" never carried.
          lastError = res.error;
          // …unless the platform REFUSED it: a deterministic kind gets one attempt, no backoff.
          if (DETERMINISTIC_KINDS.has(res.kind)) break;
        } else {
          status = res.status;
          // The platform hands back a body only for a 2xx (see `DownloadBytesResponse.bytes`),
          // so an R2 XML error document can never be stored as the asset.
          if (status >= 200 && status < 300) {
            if (res.bytes.length > 0) return res.bytes;
            lastError = emptyBodyReason(url);
            emptyBody = true;
          } else {
            lastError = statusReason(url, res.status, res.statusText);
          }
        }
      } else {
        const res = await fetchWithTimeout(url, NO_HTTP_CACHE, DOWNLOAD_TIMEOUT_MS, "Baked asset download");
        status = res.status;
        if (res.ok) {
          const buf = new Uint8Array(await res.arrayBuffer());
          // A 200 with no body is not a usable asset: never cache zero bytes as if the download
          // had succeeded, and retry it — `emptyBody` is what makes this attempt transient.
          if (buf.length > 0) return buf;
          lastError = emptyBodyReason(url);
          emptyBody = true;
        } else {
          // Status must be CHECKED, not just reported: R2 answers a missing object with an XML
          // error document, `arrayBuffer()` resolves happily, and those bytes were previously
          // stored AS the baked asset — a missing file silently became a corrupt one.
          lastError = statusReason(url, res.status, res.statusText);
        }
      }
    } catch (e) {
      // Every failure NAMES THE URL. A bare `fetch` rejects with the browser's
      // `TypeError: Failed to fetch` — no url, no status, no cause — which is the useless
      // string that used to reach the operator's error log.
      //
      // WebKit collapses EVERY transport failure into the same status-less `Load failed`, so
      // the causes have to be LISTED, in likelihood order, rather than guessed at. The order
      // below was learned the hard way: this hint used to send operators to the R2 bucket's
      // CORS policy first, and the CORS policy was verified CORRECT from `tauri://localhost`,
      // `http://tauri.localhost` and the web origin — an explicit OPTIONS probe answered 204
      // and the object came back 200 in 1.4 s. (That probe was hand-issued; see below — this
      // request is a simple GET and never triggers one of its own.)
      // Every minute spent there was a minute not spent on the device's own network.
      //
      // CORS comes LAST, and the symptom stated for it must be TRUE from here. Two things
      // this hint used to get wrong (review 1, M-3):
      //   * There is NO preflight to fail. This is `fetchWithTimeout(url, NO_HTTP_CACHE, …)` above — a
      //     bare cross-origin GET with no custom headers, i.e. a CORS *simple request*. The
      //     browser never issues an OPTIONS for it, so "only when the preflight fails" read
      //     as "CORS is never the cause here".
      //   * A CORS-blocked response gives JavaScript NO status. `fetch` rejects with a
      //     status-less TypeError; `status` stays `undefined` in this loop and the operator
      //     sees the same `Load failed`. The status exists on the wire and in devtools — it
      //     is simply not reachable from where this message is built.
      // What DOES distinguish CORS is its BLAST RADIUS: a bucket policy fails every template
      // on every device at once (a cached `<img>` copy is the exception; see NO_HTTP_CACHE).
      const why = e instanceof Error ? e.message : String(e);
      lastError =
        `GET ${loggableUrl(url)} failed (${why}) — no HTTP status reached this code, so the request never ` +
        "completed. Likeliest first: this device's network path (DNS, a VPN or a proxy); " +
        "then a stale connection inside the app's own web view (quit and reopen the app); " +
        "then the object being missing or forbidden, though that normally arrives instead as " +
        "a 404 or 403 WITH a status; and last the bucket's CORS policy — a CORS refusal looks " +
        "exactly like a network failure here, with no status either, but it fails EVERY " +
        "template at once and on EVERY device, so a failure narrower than that is not CORS";
    }
    if (attempt === DOWNLOAD_ATTEMPTS || !(emptyBody || isRetryableStatus(status))) break;
    await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS[attempt - 1] ?? 1200));
  }
  // The suffix reports what was DONE: a definite answer (404, 403) is one attempt, not the
  // ladder's ceiling — the old text claimed three attempts for every failure.
  throw new Error(`${lastError} (after ${made} attempt${made === 1 ? "" : "s"})`);
}

export function ioHydrateDeps(io: {
  decodeImage: (bytes: Uint8Array) => Promise<{ width: number; height: number; rgba: ArrayLike<number> }>;
  /** WS-W: present on desktop only. Feature-detected here rather than branched on `io.kind`, so
   *  a platform that has not got it (web, tests, a plain-browser dev session) keeps `fetch`. */
  downloadBytes?: PlatformIO["downloadBytes"];
}): HydrateDeps {
  const downloadBytes = io.downloadBytes?.bind(io);
  return {
    fetchBytes: (url) => fetchBytesWithRetry(url, downloadBytes),
    decodeMask: async (bytes) => {
      const { width, height, rgba } = await io.decodeImage(bytes);
      return decodeMaskCoverage(rgba, width, height);
    },
  };
}

/** The preview URL for a version's baked assets (the generated image), or undefined.
 *  MAP-AWARE: the flat fields only mirror the PRIMARY ("back") view — a template whose only
 *  baked view is front/side has empty flat fields, so fall through to the first per-view
 *  entry that carries a preview/generated URL (preview-images bug: thumbnails and the
 *  drawer hero rendered placeholders for any non-primary-only bake). */
export function bakedPreviewUrl(refs?: (BakedAssetRefs & { views?: Record<string, BakedAssetRefs | null> }) | null): string | undefined {
  if (!refs) return undefined;
  const flat = refs.preview_url || refs.generated_url;
  if (flat) return flat;
  for (const v of Object.values(refs.views ?? {})) {
    const u = v?.preview_url || v?.generated_url;
    if (u) return u;
  }
  return undefined;
}

/** Whether a baked-asset map carries at least one REAL baked view — a `generated_url` or
 *  `shell_url` on the flat/primary fields OR in any per-view entry. A template with none of
 *  these has no mockup shell and would export design-only, so Save gates on this. */
export function hasAnyBakedView(refs: BakedAssetRefsMap | null | undefined): boolean {
  const baked = (r: BakedAssetRefs | undefined | null): boolean => !!(r && (r.generated_url || r.shell_url));
  if (!refs) return false;
  if (baked(refs)) return true;
  return Object.values(refs.views ?? {}).some(baked);
}

/**
 * Hydrate ONE view's baked refs into LOCAL `MockupAssets` (putMockup), with the A1
 * dedup/failure-memo/status machinery keyed per (version, view) via `mockupKey`. Returns
 * true when that view's assets are now local; false when a memoized failure short-circuits;
 * rejects when the fetch/decode fails (memoizing the failure once). `refs` here is the flat
 * per-view `BakedAssetRefs` already resolved by `refsForView`.
 */
/**
 * The engine's OWN `ShadingParams` defaults, mirrored field for field from
 * `engine/genz-engine/src/model/render_settings.rs` — `impl Default for ShadingParams` plus the
 * `d_shade_gain()` = 2.0 / `d_shadow_boost()` = 1.0 helpers immediately above it. A field repaired
 * to one of these renders exactly as if the key had been absent, which is the engine's own
 * behaviour for an omitted key. `min` / `max` are deliberately NOT here: an unusable clamp window
 * costs us the whole object (below), which is the stronger response and the one already pinned.
 *
 * If the engine retunes a default, retune it here too — the comment on each line is the citation,
 * and `bakedSync.hydrate.test.ts` parses that Rust block and fails if any value here drifts.
 * The key type is the engine's own field list minus the three handled elsewhere, so a dropped or
 * misspelt key is a type error, not a silently unguarded field (review-2 N-1).
 *
 * `strength` is deliberately NOT here either (review-2 N-2): it is the master switch, and a
 * present `ShadingParams` object beats the legacy `print_shading_strength` scalar in the engine
 * (`mockup.rs:871`), so "repairing" a non-finite strength to 0 would guarantee a flat print area
 * AND suppress the calibrated scalar the same row carries. It goes with `min` / `max`: unusable
 * ⇒ the whole object is dropped and the engine's defaults apply.
 */
type ShadingScalarField = Exclude<keyof ShadingParams, "min" | "max" | "linear" | "strength">;
export const SHADING_FIELD_DEFAULTS: Readonly<Record<ShadingScalarField, number>> = {
  gain: 2, //            render_settings.rs `gain: d_shade_gain()` = 2.0
  detailGain: 2, //      render_settings.rs `detail_gain: d_shade_gain()` = 2.0
  lowpass: 0, //         render_settings.rs `lowpass: 0.0`
  gradient: 0, //        render_settings.rs `gradient: 0.0`
  grain: 0, //           render_settings.rs `grain: 0.0`
  shadowBoost: 1, //     render_settings.rs `shadow_boost: d_shadow_boost()` = 1.0
  highlightBoost: 1, //  render_settings.rs `highlight_boost: d_shadow_boost()` = 1.0
  sheen: 0, //           render_settings.rs `sheen: 0.0`
  highlights: 0, //      render_settings.rs `highlights: 0.0`
  smoothing: 0, //       render_settings.rs `smoothing: 0.0`
  caseHighlights: 0, //  render_settings.rs `case_highlights: 0.0`
  inkBlack: 0, //        render_settings.rs `ink_black: 0.0`
  inkWhite: 1, //        render_settings.rs `ink_white: d_ink_white()` = 1.0
};

/**
 * Make a stored shading calibration safe to hand to the engine.
 *
 * `baked_assets.shading` is cloud JSON handed STRAIGHT to the engine, so this is the boundary
 * where untrusted numbers stop. Two different failures, two different answers:
 *
 * 1. **An unusable clamp window is dropped whole.** An inverted (`min > max`) or non-finite
 *    window used to panic Rust's `f32::clamp` mid-render — and on wasm a panic is a trap that
 *    kills the render worker for the rest of the session. The engine normalizes the window itself
 *    now (`mockup.rs:321-331`), so this is belt-and-braces: a calibration we cannot interpret is
 *    dropped (the engine's own defaults then apply) rather than silently reinterpreted.
 *
 * 2. **A non-finite SCALAR is repaired to the engine's default for that field.** This guard did
 *    not exist (review-1 F3) and the engine does not catch it either — it RENDERS it. A NaN
 *    `gradient` survives `mockup.rs:368`'s `f.clamp(lo, hi)` (clamp returns NaN for a NaN self)
 *    and then `mockup.rs:970`'s `(px * f).round().clamp(0.0, 255.0) as u8` saturates NaN to
 *    **0** — the print area renders BLACK. `grain` takes the same route via `mockup.rs:980`,
 *    whose `grain != 0.0` gate is `true` for NaN. It is reachable through ordinary JSON, which
 *    has no NaN literal but has unbounded exponents: `JSON.parse('1e400')` is `Infinity`, and
 *    `Infinity * (0.5 - ny)` is NaN at exactly `ny === 0.5`, i.e. one black scanline across the
 *    middle of every print area on that template. Dropping the whole object here would be a
 *    bigger change than the damage (it would discard a calibration whose other fields are fine),
 *    so the bad field alone falls back to the engine default.
 *
 * A repair is never silent: it logs ONE `console.warn` naming the template, and every field it
 * touched with the value it found. `hydrateOneView` is deep in a fetch/decode path with no
 * `AppLogger` in scope, so `console.warn` is the honest channel here (same shape as
 * `stores/session.ts`); the `try` is because a broken console must not fail a hydrate.
 */
function sanitizeShading(shading: ShadingParams | undefined, label: string): ShadingParams | undefined {
  if (!shading) return undefined;
  const finite = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);
  const { min, max, strength } = shading;
  if (min !== undefined && !finite(min)) return undefined;
  if (max !== undefined && !finite(max)) return undefined;
  if (finite(min) && finite(max) && min > max) return undefined;
  // The master switch: unusable ⇒ the whole calibration is dropped (see SHADING_FIELD_DEFAULTS).
  if (typeof strength === "number" && !finite(strength)) return undefined;

  // Only an actual non-finite NUMBER is repaired. A key of some other type never came from a
  // shading calibration at all and is left for the engine's own deserializer to reject.
  const repaired: string[] = [];
  let out = shading;
  for (const [field, fallback] of Object.entries(SHADING_FIELD_DEFAULTS)) {
    const v = (shading as unknown as Record<string, unknown>)[field];
    if (typeof v !== "number" || Number.isFinite(v)) continue;
    if (out === shading) out = { ...shading };
    (out as unknown as Record<string, number>)[field] = fallback;
    repaired.push(`${field}=${String(v)} → ${fallback}`);
  }
  if (repaired.length > 0) {
    try {
      console.warn(
        `bakedSync: ${label} — stored shading calibration carries non-finite value(s); ` +
        `repaired to the engine defaults (the print area would otherwise render black): ${repaired.join(", ")}`,
      );
    } catch { /* nor a broken console */ }
  }
  return out;
}

function hydrateOneView(
  versionId: string,
  view: string,
  refs: BakedAssetRefs,
  deps: HydrateDeps,
  opts?: { force?: boolean },
): Promise<boolean> {
  const key = mockupKey(versionId, view);
  // `force` must ACTUALLY force: a re-bake persisted onto the same version id would otherwise
  // be invisible forever on a device that already hydrated once — the "already local" fast
  // paths below win before the flag was ever consulted (the original force-sync audit bug).
  // Forced hydrates skip both local-presence guards and re-download; putMockup overwrites.
  if (!opts?.force && hasMockup(versionId, view)) { markReady(key); return Promise.resolve(true); } // sync mem fast path
  const memo = getFailure(key);
  if (memo && !opts?.force && Date.now() - memo.at < FAILURE_TTL_MS) return Promise.resolve(false); // memoized failure
  // In-flight dedup that RESPECTS `force`. Keyed by (version, view) alone, a forced hydrate
  // arriving while an ordinary one ran joined that run — which short-circuits on the
  // already-local fast path — so force resolved `true` having downloaded nothing and a
  // re-bake on the same version id stayed invisible. The reverse join is still allowed: a
  // forced run is strictly fresher than what an ordinary caller asked for.
  const existing = joinInflight(key, opts?.force);
  if (existing) return existing;
  const run = (async () => {
    // WS-ERR H-1 — this is a PROBE, not a read, and a probe may never fail the hydrate.
    //
    // `getMockup` is the STRICT read: F-9 made it throw when the store is broken and memory has
    // nothing to answer with, because answering `null` there means "this template has no bake"
    // and the queue renders that as a design over a blank background. Right for a read; wrong
    // here, where the only question is "can I skip a download". Unguarded, a broken store threw
    // before a single byte was fetched, the run rejected into `setFailure`, and EVERY first
    // hydrate failed — on exactly the device that needed the download most.
    //
    // `.catch(() => null)` gives the probe `hasMockupStored`'s answer ("no" on a broken store)
    // while keeping `getMockup`'s legacy bare-key migration, which a bare `hasMockupStored`
    // would miss and re-download for. The hydrate then proceeds and `putMockup` degrades to
    // memory (F-9), so the session works and the next launch downloads again.
    const alreadyLocal = opts?.force ? null : await getMockup(versionId, view).catch(() => null);
    if (alreadyLocal) { markReady(key); return true; } // already local (IndexedDB or this session's memory)
    markHydrating(key);
    // Label each leg, so a failure says WHICH asset of WHICH view died rather than just
    // surfacing one anonymous rejection out of three concurrent fetches.
    const [generated, shell, maskBytes] = await Promise.all(
      ([
        ["generated", refs.generated_url],
        ["shell", refs.shell_url],
        ["mask", refs.mask_url],
      ] as const).map(([label, assetUrl]) =>
        deps.fetchBytes(assetUrl).catch((e) => {
          const why = e instanceof Error ? e.message : String(e);
          throw new Error(`${view}/${label}: ${why}`);
        }),
      ),
    );
    const productMask = await deps.decodeMask(maskBytes);
    const printMask = refs.print_mask_url
      ? await deps.decodeMask(await deps.fetchBytes(refs.print_mask_url))
      : undefined;
    // Restore the picked LAYER pixels (texture overlay + clear-case background) so a synced
    // device renders the SAME layered mockup as the authoring device. Without this the export
    // silently loses the texture/clear-bg layers even though their URLs round-tripped.
    // NB: the clear-case background is the `phone` (behind) layer — mirrors the authoring bake
    // in MaskEditor and how the compositor consumes it (`behind = clearPhone && phone`).
    // LEGACY-POISON GUARD: records written before the durable-ref enforcement can carry
    // RELATIVE layer URLs (library rows created under local hosting — "/image-lists/…").
    // Those can never resolve on any device; fetching them verbatim failed the WHOLE
    // hydrate — and once "Force sync all" started reporting hydrate failures honestly,
    // every template carrying one showed as failed. Skip them: the core mockup still
    // syncs, only the decorative layer is omitted (re-adding the image from a properly
    // hosted library heals the record on the next bake).
    const durableUrl = (u?: string) => (u && /^https?:\/\//i.test(u) ? u : undefined);
    const textureUrl = durableUrl(refs.texture_url);
    const clearBgUrl = durableUrl(refs.clear_bg_url);
    const [fetchedTexture, phone] = await Promise.all([
      textureUrl ? deps.fetchBytes(textureUrl) : Promise.resolve(null),
      clearBgUrl ? deps.fetchBytes(clearBgUrl) : Promise.resolve(null),
    ]);
    // UV VARNISH WITH NO TEXTURE IMAGE. The effect is generated from the DESIGN — the texture layer
    // only supplies print-area COVERAGE — so the editor bakes a synthesized solid-opaque stand-in
    // and never uploads a `texture_url`. The engine's fill stack is gated on the texture BYTES
    // existing (engine-shared `if (m.texture)`), so a synced device hydrated `texture: null` and
    // dropped the varnish layer entirely: the effect rendered here and nowhere else. Substitute the
    // same coverage stand-in (a 1×1 opaque white PNG warps to the print quad exactly like the
    // full-res one the editor bakes) whenever the refs say the layer was on with a uvvarnish blend.
    const texture =
      fetchedTexture ??
      (refs.texture_on && refs.texture_blend === "uvvarnish" ? UV_VARNISH_COVERAGE_PNG : null);
    const assets: MockupAssets = {
      generated,
      shell,
      productMask,
      printMask,
      printQuad: refs.print_quad,
      clearPhone: refs.clear_phone ?? false,
      opacity: refs.opacity,
      texture,
      phone,
      // Restore the texture blend mode so the composite matches the authoring device.
      blendMode: refs.texture_blend ? { texture: refs.texture_blend } : undefined,
      // Restore the spot-UV varnish calibration so a "uvvarnish" texture composites identically.
      varnish: refs.varnish,
      // Restore the saved placement rotation so the render/export on this device matches.
      designRotation: refs.design_rotation,
      // Restore the design fill mode + forced aspect so the render/export on this device matches.
      fillFit: refs.design_fill as FitMode | undefined,
      designAspect: refs.design_aspect ?? null,
      // Restore the EFFECTIVE artwork-shading strength (raw * stage toggle) so a synced device's
      // render/export matches the authoring device — undefined when no ref was saved (engine-shared
      // `?? 0` renders flat, the sanctioned legacy behavior).
      printShadingStrength: refs.stage_shading === false ? 0 : refs.print_shading,
      // Restore the full calibration object too (it WINS over the scalar in the engine); when
      // the stage is toggled off, drop it so the scalar 0 above renders flat.
      shading: refs.stage_shading === false ? undefined : sanitizeShading(refs.shading, `${versionId}/${view}`),
    };
    await putMockup(versionId, assets, view);
    markReady(key);
    return true;
  })().catch((e) => {
    // Memoize the failure ONCE; every awaiter of `run` still receives the rejection to handle.
    setFailure(key, e instanceof Error ? e.message : String(e));
    throw e;
  });
  // The store owns the entry's whole lifecycle (including dropping the forced flag when the
  // run settles), so a stuck entry cannot outlive the request that created it.
  trackInflight(key, run, opts?.force);
  return run;
}

/**
 * Resolve a version's cloud baked-asset refs into LOCAL `MockupAssets` (putMockup),
 * so the layered render/export works on this device. No-op when there are no refs or
 * the assets are already local. Returns true when every requested view WITH refs is now local.
 *
 * VIEWS (Task B2): default = the PRIMARY (back) view only — today's behavior. With
 * `opts.views` it hydrates each listed view's refs (resolved via `refsForView`); a listed
 * view that carries NO refs is skipped, not a failure. Each view runs through the A1
 * dedup/failure-memo/status machinery keyed per (version, view) via `mockupKey`.
 *
 * AGGREGATE STATUS: the per-view status lands under each `mockupKey`, but this ALSO publishes
 * an aggregate status under the BARE `versionId` (hydrating while any view hydrates; failed if
 * any view failed; ready once all requested views settle ok) so the existing row spinner (A2),
 * which subscribes to `status[versionId]`, keeps working unchanged.
 *
 * Rejection semantics are preserved from A1: a view fetch/decode failure rejects the returned
 * Promise (callers `.catch()` it) and memoizes the per-view failure once.
 */
export async function hydrateBakedAssets(
  versionId: string,
  refs: BakedAssetRefsMap | null | undefined,
  deps: HydrateDeps,
  opts?: { force?: boolean; views?: string[] },
): Promise<boolean> {
  // EVERY `false` this function returns must leave a REASON behind. A bare `false` is
  // indistinguishable from a failed download to the caller, and `autoSync` printed
  // "download reported failure without an error" over exactly these paths (WS-V).
  if (!versionId) return false; // nothing to record it against
  if (!refs) {
    // DEAD from production: all four callers (`autoSync`, `TemplateListPage` x2,
    // `TemplateEditor`, `BatchContents`) already guard on `refs` and route the absence through
    // `announceBakeAbsence`, which renders it as ABSENT rather than failed. Kept as a defensive
    // floor for a future caller — but note that if it ever did run it would mark the row
    // FAILED, which is not what "publishes nothing" should look like (review 1, L-4).
    setFailure(versionId, "this template version publishes no baked assets");
    return false;
  }
  const requested = opts?.views && opts.views.length > 0 ? [...new Set(opts.views)] : [PRIMARY_VIEW];
  // Resolve each requested view's refs; a view with none is SKIPPED (not a failure).
  const targets = requested
    .map((view) => ({ view, viewRefs: refsForView(refs, view) }))
    .filter((t): t is { view: string; viewRefs: BakedAssetRefs } => !!t.viewRefs);
  if (targets.length === 0) {
    // Nothing to hydrate: the version says it saved these views, but none of them carries
    // baked refs. That is a publishing gap, not a download failure — and saying so is the
    // difference between a fixable report and a shrug.
    setFailure(versionId, `no baked assets published for view(s): ${requested.join(", ")}`);
    return false;
  }

  // Aggregate under the bare versionId so A2's row spinner keeps working. Only flip to
  // "hydrating" when a view actually needs pulling (mirrors the old sync fast path — an
  // already-local set goes straight to "ready" without a spinner flash).
  const anyPending = targets.some((t) => !hasMockup(versionId, t.view));
  if (anyPending) markHydrating(versionId);
  try {
    // Promise.all preserves A1's reject-on-failure contract; each view dedups independently.
    const results = await Promise.all(
      targets.map((t) => hydrateOneView(versionId, t.view, t.viewRefs, deps, opts)),
    );
    const allLocal = results.every((r) => r === true);
    if (allLocal) {
      markReady(versionId);
    } else {
      // A memoized per-view failure short-circuited (returned false, no throw). Surface it on
      // the aggregate so the row still reflects the unresolved view.
      const failedKey = targets
        .map((t) => mockupKey(versionId, t.view))
        .find((k) => getFailure(k));
      setFailure(versionId, failedKey ? getFailure(failedKey)!.message : "hydration incomplete");
    }
    return allLocal;
  } catch (e) {
    setFailure(versionId, e instanceof Error ? e.message : String(e));
    throw e;
  }
}

// --- Producer (bake → upload → cloud refs) ---------------------------------

/** The baked pixels + geometry to publish for cross-device sync. */
export interface BakedUploadInput {
  generated: Uint8Array;
  shell: Uint8Array;
  /** Product mask encoded as a grayscale PNG (255 inside the product). */
  maskPng: Uint8Array;
  /** Print-area mask as a grayscale PNG (255 inside the print region). Optional — when
   *  present, synced devices clip the design to the print area (clear-case fix). */
  printMaskPng?: Uint8Array;
  printQuad: Polygon;
  clearPhone?: boolean;
  opacity?: MockupAssets["opacity"];
  /** Hosted library URLs of the texture / clear-case-background layers + the texture
   *  blend, threaded through so they persist to the cloud refs for cross-device restore. */
  textureUrl?: string;
  clearBgUrl?: string;
  /** Clear-case layer bytes with no hosted URL yet; hosted with the bake as `clear_bg_url`. */
  clearBg?: Uint8Array;
  /** Whether the "Texture / effect layer" was ON for this bake (see `BakedAssetRefs.texture_on`).
   *  Persisted independently of `textureUrl` — a UV-varnish layer legitimately has no image. */
  textureOn?: boolean;
  textureBlend?: string;
  /** Spot-UV varnish calibration for a "uvvarnish"-blend texture layer. */
  varnish?: VarnishParams;
  designRotation?: number;
  /** How the design fills the print area (see ComposeMockupArgs.fillFit). */
  fillFit?: string;
  /** Forced design aspect [w,h], center-cropped; null/omitted ⇒ original. */
  designAspect?: [number, number] | null;
  /** RAW artwork-shading strength control (0..1, NOT the baked effective value) + pipeline-stage
   *  toggles + matte quality, persisted so reopening the template restores the exact control
   *  values. `false`/`0` are meaningful (a stage toggled OFF, or shading at 0) and must persist. */
  printShadingStrength?: number;
  /** Full engine shading calibration object (see BakedAssetRefs.shading). */
  shading?: ShadingParams;
  stageDespill?: boolean;
  stageRefine?: boolean;
  stageMatteStrength?: boolean;
  stageShading?: boolean;
  matteQuality?: "fast" | "quality";
  /** Raw AI-matte encoded as a grayscale PNG (255 = kept object). Uploaded so reopening
   *  the template restores the removed state + the AI-edge calibration matte. */
  mattePng?: Uint8Array;
  /** AI-edge toggle + protect-content slider, persisted so they restore on reopen. */
  aiEdge?: boolean;
  aiEdgeKeep?: number;
  /** Per-template edge-calibration sliders (edgeFeather 0..10, edgeChoke −5..10,
   *  matteStrength 0..100), persisted so reopening restores the calibrated cutout.
   *  0/negative survive (a saved 0/−N must override the global calibration default). */
  edgeFeather?: number;
  edgeChoke?: number;
  matteStrength?: number;
  /** Which technique produced the cutout (see `BakedAssetRefs.matte_kind`). Travels with the
   *  matte so the reopen restore knows to re-key instead of replaying the grey plane. */
  matteKind?: "ai" | "chroma";
  /** Marker keying inputs + "Protect product" + the product category (see the matching
   *  `BakedAssetRefs` fields). All three reshape the baked shell/print mask, and the cloud persist
   *  FULL-OVERWRITES the dict, so they must travel with every bake or the next Save erases them. */
  markerColor?: [number, number, number] | null;
  markerTol?: number;
  shadedMagentaGuide?: boolean;
  protectProduct?: boolean;
  productCategory?: string;
  /** The version's EXISTING baked-asset refs (as last loaded/saved). Used for matte CARRY-FORWARD:
   *  when THIS bake produces no live matte (`mattePng` absent) but these refs already carry a hosted
   *  `matte_url` + AI-edge state, they are preserved into the returned refs instead of dropped —
   *  because the cloud `setBakedAssets` FULL-OVERWRITES the dict, so a matte-less re-bake would
   *  otherwise WIPE the removal state a later reopen needs to restore. Ignored when `mattePng` is
   *  present (the fresh matte wins) or `matteCleared` is set (a deliberate clear). */
  previousRefs?: BakedAssetRefs | null;
  /** The user DELIBERATELY cleared the AI removal this session ("Restore background"). Suppresses
   *  carry-forward so the matte fields are intentionally dropped, not resurrected from `previousRefs`. */
  matteCleared?: boolean;
}

export interface UploadDeps {
  /** Presign one hosted PNG target (e.g. client.templateReferenceUpload → R2). */
  presign: () => Promise<{ upload_url: string; public_url: string; fields?: Record<string, string> }>;
  /** PUT the bytes to a presigned target. */
  putBytes: (uploadUrl: string, bytes: Uint8Array, fields?: Record<string, string>) => Promise<void>;
}

/** Verification outcome for one hosted object (see `verifyObject`). */
type VerifyOutcome = "verified" | "missing" | "unverifiable";

function hostOf(url: string): string {
  try { return new URL(url).host; } catch { return url; }
}

/**
 * Verify a hosted object landed, via a HEAD→ranged-GET decision LADDER. A PUT can "succeed"
 * while the object is missing (a local-hosting no-op, or a presign that expired mid-flight —
 * R2 presigns have a 900s TTL), which persists refs other devices then 404 on → design-only
 * render. So each render-critical object is confirmed fetchable BEFORE its ref is persisted.
 *
 * The ladder distinguishes a DEFINITE miss (retry-and-maybe-throw) from an UNVERIFIABLE probe
 * (a CORS/network-blind check that can't prove presence OR absence — must NOT block persist):
 *  1. Non-absolute http(s) URL → "missing" (a ref nobody can resolve is itself the bug).
 *  2. HEAD returns an HTTP response: `res.ok` → "verified"; any non-ok (404/403/other) →
 *     "missing" (conservative — a real HTTP answer is authoritative).
 *  3. HEAD REJECTED (no HTTP status — network/CORS; some hosts serve GET but block cross-origin
 *     HEAD): fall through to a ranged GET of the first byte. `res.ok || 206` → "verified";
 *     a non-ok HTTP answer → "missing".
 *  4. The ranged GET ALSO rejected (still no HTTP status — pure network/CORS): "unverifiable".
 *     The caller must PROCEED to persist: the PUTs succeeded moments ago on this same network,
 *     so blocking persist on a CORS-blind verify recreates the silent-partial-sync bug in the
 *     opposite direction (every save banners, refs never persist).
 */
async function verifyObject(url: string): Promise<VerifyOutcome> {
  if (!/^https?:\/\//i.test(url)) return "missing"; // not an absolute http(s) URL → unresolvable
  // Rung 1: HEAD. A returned HTTP response is authoritative either way.
  let headRes: Response | undefined;
  try {
    headRes = await fetchWithTimeout(url, { ...NO_HTTP_CACHE, method: "HEAD" }, DOWNLOAD_TIMEOUT_MS, "Asset HEAD check");
  } catch {
    headRes = undefined; // network/CORS rejection — no HTTP status; drop to the ranged GET
  }
  if (headRes) return headRes.ok ? "verified" : "missing";
  // Rung 2: HEAD gave no HTTP status. Some hosts serve GET but reject cross-origin HEAD, so try
  // a single-byte ranged GET. 206 (partial) or a plain 200 both prove the object is fetchable.
  let getRes: Response | undefined;
  try {
    getRes = await fetchWithTimeout(url, { ...NO_HTTP_CACHE, method: "GET", headers: { Range: "bytes=0-0" } },
      DOWNLOAD_TIMEOUT_MS, "Asset range check");
  } catch {
    getRes = undefined; // still no HTTP status
  }
  if (getRes) {
    const ok = getRes.ok || getRes.status === 206;
    // We only needed the status line; release the body stream this GET opened so it isn't left
    // dangling (hygiene on a hot path). Optional-chained + try/guarded — jsdom Responses have no
    // real body/cancel, so this must never throw.
    try { void getRes.body?.cancel(); } catch { /* body/cancel unsupported (jsdom) — ignore */ }
    return ok ? "verified" : "missing";
  }
  // Rung 3: neither probe produced an HTTP status (pure network/CORS blindness). Cannot prove the
  // object is absent — classify UNVERIFIABLE so the caller still persists (see the doc comment).
  return "unverifiable";
}

/**
 * Upload the baked pixel assets (generated/shell/mask) to hosting and return the
 * cloud `BakedAssetRefs` to persist via `client.setBakedAssets`. This is the producer
 * counterpart to `hydrateBakedAssets`: it puts the layered-mockup assets where every
 * device can resolve them, so exports carry all layers cross-device.
 *
 * F2: all presigns (3–6) issue in ONE `Promise.all` batch (no serial round-trips), then after
 * the PUTs it verifies EVERY render-critical object in parallel via the HEAD→ranged-GET ladder
 * (`verifyObject`) — the shell/generated are exactly what a design-only render is missing, so a
 * single representative is not enough. Each DEFINITE miss re-PUTs once with a FRESH presign
 * (covers an expired presign) and re-verifies; still missing ⇒ throw, so the caller NEVER
 * persists refs nobody can resolve. An UNVERIFIABLE (CORS/network-blind) result is logged and
 * proceeds — the PUT just succeeded on this same network, so it must not block persist. Refs
 * point at whichever URL was actually verified/re-uploaded.
 */
export async function uploadBakedAssets(
  input: BakedUploadInput,
  deps: UploadDeps,
  logger?: AppLogger,
): Promise<BakedAssetRefs> {
  // Presign ALL targets in one concurrent batch — generated/shell/mask always, plus the print
  // mask (clear-case fix) and the raw AI matte (restores calibration on reopen) when present.
  // Serial `await`s here cost up to 3 extra RTTs on every bake (incl. the auto-bake after each
  // removal), so they are issued together and unpacked positionally afterwards.
  const wantPm = !!input.printMaskPng;
  const wantMt = !!input.mattePng;
  const wantCb = !!input.clearBg;
  const presigns = await Promise.all([
    deps.presign(), deps.presign(), deps.presign(),
    ...(wantPm ? [deps.presign()] : []),
    ...(wantMt ? [deps.presign()] : []),
    ...(wantCb ? [deps.presign()] : []),
  ]);
  let pi = 0;
  const g = presigns[pi++]!, s = presigns[pi++]!, m = presigns[pi++]!;
  const pm = wantPm ? presigns[pi++]! : null;
  const mt = wantMt ? presigns[pi++]! : null;
  const cb = wantCb ? presigns[pi++]! : null;

  await Promise.all([
    deps.putBytes(g.upload_url, input.generated, g.fields),
    deps.putBytes(s.upload_url, input.shell, s.fields),
    deps.putBytes(m.upload_url, input.maskPng, m.fields),
    ...(pm && input.printMaskPng ? [deps.putBytes(pm.upload_url, input.printMaskPng, pm.fields)] : []),
    ...(mt && input.mattePng ? [deps.putBytes(mt.upload_url, input.mattePng, mt.fields)] : []),
    ...(cb && input.clearBg ? [deps.putBytes(cb.upload_url, input.clearBg, cb.fields)] : []),
  ]);

  /** One render-critical object to verify: its current URL + the bytes to re-PUT on a miss. */
  interface Uploaded { name: string; bytes: Uint8Array; url: string; }
  const toVerify: Uploaded[] = [
    { name: "generated", bytes: input.generated, url: g.public_url },
    { name: "shell", bytes: input.shell, url: s.public_url },
    { name: "mask", bytes: input.maskPng, url: m.public_url },
    ...(pm && input.printMaskPng ? [{ name: "print_mask", bytes: input.printMaskPng, url: pm.public_url }] : []),
    ...(mt && input.mattePng ? [{ name: "matte", bytes: input.mattePng, url: mt.public_url }] : []),
    ...(cb && input.clearBg ? [{ name: "clear_bg", bytes: input.clearBg, url: cb.public_url }] : []),
  ];

  // Verify one object; on a DEFINITE miss re-PUT once with a fresh presign and re-verify. Returns
  // the URL to persist (the fresh one after a successful retry). Throws only when an object is
  // still a definite miss after the retry. Unverifiable (CORS-blind) never retries or throws.
  const verifyOne = async (a: Uploaded): Promise<string> => {
    const first = await verifyObject(a.url);
    if (first === "verified") return a.url;
    if (first === "unverifiable") {
      // Could not prove presence OR absence (CORS/network). The PUT succeeded moments ago on this
      // same network, so persist anyway — blocking here recreates the silent-partial-sync bug.
      // INFO, not warn: this fires 3-6× on EVERY bake when the bucket CORS blocks HEAD
      // probes (the documented common case) and the outcome is benign — persisting anyway.
      logger?.log("info", "Sync: baked asset unverifiable (CORS/network-blind) — persisting anyway", {
        url: hostOf(a.url), kind: "unverifiable", asset: a.name,
      });
      return a.url;
    }
    // first === "missing": a definite miss. Re-PUT once with a fresh presign, then re-verify.
    logger?.log("warn", "Sync: baked asset missing after PUT — retrying with a fresh presign", {
      url: hostOf(a.url), kind: "missing", asset: a.name,
    });
    const fresh = await deps.presign();
    await deps.putBytes(fresh.upload_url, a.bytes, fresh.fields);
    const second = await verifyObject(fresh.public_url);
    if (second === "missing") {
      throw new Error(`baked asset not fetchable after retry (${a.name}: ${a.url})`);
    }
    // Split the breadcrumb wording by the post-retry verdict: a re-PUT that VERIFIES is genuinely
    // "verified", but an UNVERIFIABLE (CORS/network-blind) verdict is being persisted on faith —
    // saying "verified" there would overstate what we actually confirmed. (The error-log warn above
    // still records the unverifiable case in full for the ERROR trail; this is the activity trail.)
    if (second === "unverifiable") {
      logger?.log("info", "Sync: baked asset unverifiable after retry — persisting the fresh ref", {
        url: hostOf(fresh.public_url), kind: "unverifiable", asset: a.name,
      });
      logger?.breadcrumb("action", "Sync: baked asset re-uploaded (unverifiable — persisting the fresh ref)", {
        asset: a.name, host: hostOf(fresh.public_url),
      });
    } else {
      logger?.breadcrumb("action", "Sync: baked asset re-uploaded + verified", {
        asset: a.name, host: hostOf(fresh.public_url),
      });
    }
    return fresh.public_url; // adopt the fresh URL for the ref
  };

  // Verify EVERY object in parallel (Promise.all rejects on the first definite-miss-after-retry).
  const verifiedUrls = await Promise.all(toVerify.map(verifyOne));
  let ri = 0;
  const generatedUrl = verifiedUrls[ri++]!;
  const shellUrl = verifiedUrls[ri++]!;
  const maskUrl = verifiedUrls[ri++]!;
  const printMaskUrl = pm ? verifiedUrls[ri++]! : undefined;
  const matteUrl = mt ? verifiedUrls[ri++]! : undefined;
  const hostedClearBgUrl = cb ? verifiedUrls[ri++]! : undefined;
  logger?.breadcrumb("action", "Sync: all baked assets verified", { count: toVerify.length });

  // Matte fields (matte_url + AI-edge toggle + calibration sliders). Three cases:
  //  1. FRESH matte this bake (`mattePng` present) → the just-uploaded matte + this bake's flags win.
  //  2. NO live matte, but `previousRefs` already carries a hosted matte AND the user did NOT
  //     deliberately clear the removal → CARRY FORWARD the previous refs' matte fields, so the
  //     cloud's full-overwrite persist doesn't wipe the removal state a later reopen restores.
  //  3. Otherwise (never removed, or a deliberate "Restore background") → emit nothing (matte stays
  //     off). `!== undefined` (not truthiness): 0 / negative choke are valid saved slider values.
  const carryForward = !input.mattePng && !input.matteCleared && !!input.previousRefs?.matte_url;
  let matteFields: Partial<BakedAssetRefs>;
  if (matteUrl) {
    matteFields = {
      matte_url: matteUrl,
      ...(input.aiEdge !== undefined ? { ai_edge: input.aiEdge } : {}),
      ...(input.aiEdgeKeep !== undefined ? { ai_edge_keep: input.aiEdgeKeep } : {}),
      ...(input.edgeFeather !== undefined ? { edge_feather_px: input.edgeFeather } : {}),
      ...(input.edgeChoke !== undefined ? { edge_choke_px: input.edgeChoke } : {}),
      ...(input.matteStrength !== undefined ? { matte_strength: input.matteStrength } : {}),
      ...(input.matteKind !== undefined ? { matte_kind: input.matteKind } : {}),
    };
  } else if (carryForward) {
    const prev = input.previousRefs!;
    matteFields = {
      matte_url: prev.matte_url,
      ...(prev.ai_edge !== undefined ? { ai_edge: prev.ai_edge } : {}),
      ...(prev.ai_edge_keep !== undefined ? { ai_edge_keep: prev.ai_edge_keep } : {}),
      ...(prev.edge_feather_px !== undefined ? { edge_feather_px: prev.edge_feather_px } : {}),
      ...(prev.edge_choke_px !== undefined ? { edge_choke_px: prev.edge_choke_px } : {}),
      ...(prev.matte_strength !== undefined ? { matte_strength: prev.matte_strength } : {}),
      ...(prev.matte_kind !== undefined ? { matte_kind: prev.matte_kind } : {}),
    };
    logger?.breadcrumb("action", "bake · carrying forward matte refs (no live matte this bake)", {
      keys: Object.keys(matteFields), host: hostOf(prev.matte_url!),
    });
  } else {
    matteFields = {};
  }

  // The pass-through layer URLs skip the verify ladder ("already on the CDN"), but they must
  // still be DURABLE refs: a library row created under local hosting carries a root-relative
  // `/image-lists/...` URL that would persist cleanly and then 404 on every device (including,
  // eventually, this one). Drop such a ref WITH a warning rather than poisoning the record —
  // the pixel assets above still sync, only the decorative layer is omitted.
  const durable = (url: string | undefined, name: string): string | undefined => {
    if (!url) return undefined;
    if (/^https?:\/\//i.test(url)) return url;
    logger?.log("warn", `Sync: ${name} is not a hosted http(s) URL — dropping it from the synced refs (other devices would 404 on it). Re-add the image from a properly-hosted library.`, {
      kind: "non-durable-ref", asset: name, url: url.slice(0, 64),
    });
    return undefined;
  };
  const textureUrl = durable(input.textureUrl, "texture_url");
  const clearBgUrl = hostedClearBgUrl ?? durable(input.clearBgUrl, "clear_bg_url");

  return {
    generated_url: generatedUrl,
    shell_url: shellUrl,
    mask_url: maskUrl,
    ...(printMaskUrl ? { print_mask_url: printMaskUrl } : {}),
    ...matteFields,
    print_quad: input.printQuad,
    clear_phone: input.clearPhone,
    opacity: input.opacity,
    // Pass-through hosted layer URLs (already on the CDN — no re-upload needed) so the
    // texture / clear-case-background layers restore when the template opens elsewhere.
    ...(textureUrl ? { texture_url: textureUrl } : {}),
    ...(clearBgUrl ? { clear_bg_url: clearBgUrl } : {}),
    // `!== undefined` (NOT truthy): the layer being OFF is a real, persistable state, and the cloud
    // persist full-overwrites — an omitted key here would read as "unknown" on the next reopen.
    ...(input.textureOn !== undefined ? { texture_on: input.textureOn } : {}),
    ...(input.textureBlend ? { texture_blend: input.textureBlend } : {}),
    ...(input.varnish ? { varnish: input.varnish } : {}),
    ...(input.designRotation ? { design_rotation: input.designRotation } : {}),
    ...(input.fillFit ? { design_fill: input.fillFit } : {}),
    ...(input.designAspect ? { design_aspect: input.designAspect } : {}),
    // `!== undefined` (NOT truthy) guards: `false`/`0` are meaningful (a stage toggled OFF, or
    // shading calibrated to 0) and must persist rather than being dropped like an unset field.
    ...(input.printShadingStrength !== undefined ? { print_shading: input.printShadingStrength } : {}),
    ...(input.shading ? { shading: input.shading } : {}),
    ...(input.stageDespill !== undefined ? { stage_despill: input.stageDespill } : {}),
    ...(input.stageRefine !== undefined ? { stage_refine: input.stageRefine } : {}),
    ...(input.stageMatteStrength !== undefined ? { stage_matte_strength: input.stageMatteStrength } : {}),
    ...(input.stageShading !== undefined ? { stage_shading: input.stageShading } : {}),
    ...(input.matteQuality ? { matte_quality: input.matteQuality } : {}),
    // Marker keying / protect-product / product category. Same `!== undefined` discipline: `false`
    // and `0` are real saved states, and an omitted key reads as "unset" on the next reopen. A
    // `markerColor` of `null` means "no pick — engine default", which is exactly what an ABSENT
    // key restores, so null is deliberately NOT written (nothing distinguishes it from unset).
    ...(input.markerColor ? { marker_color: input.markerColor } : {}),
    ...(input.markerTol !== undefined ? { marker_tol: input.markerTol } : {}),
    // Only `true` is written: the v0.2.116 cloud forbids the unknown key, and absent reads as false.
    ...(input.shadedMagentaGuide ? { shaded_magenta_guide: true } : {}),
    ...(input.protectProduct !== undefined ? { protect_product: input.protectProduct } : {}),
    ...(input.productCategory ? { product_category: input.productCategory } : {}),
  };
}

/** Origin and path, the query shown as `?…` — the way `download.rs` writes a url down. A
 *  presigned query carries `X-Amz-Signature`, and these lines reach logs and `/fail`. */
function loggableUrl(url: string): string {
  const [head, query] = String(url).split("?", 2);
  return query === undefined ? head : `${head}?…`;
}
